export interface SafeParseResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  raw?: string;
}

/**
 * Safely parse JSON with graceful error handling
 */
export function safeJsonParse<T = any>(
  input: string | unknown
): SafeParseResult<T> {
  try {
    // If input is already an object, return it
    if (typeof input === 'object' && input !== null) {
      return { success: true, data: input as T };
    }

    // Convert to string and parse
    const jsonString = typeof input === 'string' ? input : String(input);
    
    // Handle empty or whitespace-only strings
    if (!jsonString.trim()) {
      return { 
        success: false, 
        error: 'Empty input string',
        raw: jsonString 
      };
    }

    // Try to parse JSON
    const parsed = JSON.parse(jsonString);
    return { success: true, data: parsed };
    
  } catch (parseError) {
    return {
      success: false,
      error: `JSON parse failed: ${parseError instanceof Error ? parseError.message : String(parseError)}`,
      raw: typeof input === 'string' ? input : String(input)
    };
  }
}

/**
 * Safely parse API responses with fallback to raw text
 */
export function parseApiResponse<T = any>(
  response: string
): SafeParseResult<T> {
  const result = safeJsonParse<T>(response);
  
  if (!result.success) {
    // If JSON parsing fails, return the raw response as data
    return {
      success: true,
      data: response as T,
      error: `JSON parsing failed, returned raw response: ${result.error}`
    };
  }
  
  return result;
}

/**
 * Parse streaming response chunks safely
 */
export function parseStreamChunk(chunk: string): {
  type: 'data' | 'error' | 'complete';
  content?: any;
  error?: string;
} {
  try {
    // Handle server-sent events format
    if (chunk.startsWith('data: ')) {
      const data = chunk.slice(6).trim();
      
      if (data === '[DONE]') {
        return { type: 'complete' };
      }
      
      const parsed = safeJsonParse(data);
      if (parsed.success) {
        return { type: 'data', content: parsed.data };
      } else {
        return { type: 'data', content: data };
      }
    }
    
    // Handle raw JSON
    const parsed = safeJsonParse(chunk);
    if (parsed.success) {
      return { type: 'data', content: parsed.data };
    } else {
      return { type: 'data', content: chunk };
    }
    
  } catch (error) {
    return {
      type: 'error',
      error: `Failed to parse chunk: ${error instanceof Error ? error.message : String(error)}`
    };
  }
}

/**
 * Safely extract error information from API responses
 */
export function extractApiError(response: any): string {
  if (typeof response === 'string') {
    return response;
  }
  
  if (response?.error) {
    return typeof response.error === 'string' ? response.error : response.error.message || 'Unknown error';
  }
  
  if (response?.message) {
    return response.message;
  }
  
  return 'An unknown error occurred';
}

/**
 * Type guard to check if response has expected structure
 */
export function hasProperty<T extends Record<string, any>>(
  obj: any,
  key: keyof T
): obj is T {
  return obj && typeof obj === 'object' && key in obj;
}

/**
 * Safely access nested properties with fallback
 */
export function safeGet<T>(
  obj: any,
  path: string,
  fallback?: T
): T | undefined {
  try {
    const keys = path.split('.');
    let current = obj;
    
    for (const key of keys) {
      if (current == null || typeof current !== 'object') {
        return fallback;
      }
      current = current[key];
    }
    
    return current ?? fallback;
  } catch {
    return fallback;
  }
} 